import { useCallback, useEffect, useState } from "react";

import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { ContentLocationOutputDTO, MeetingOutputDTO, VodOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { Button, Loading, MultipleVideoPlayer, Touchable, VideoSourceDated } from "@kalyzee/kast-web-sdk/web-components";
import { PageMasterMode, usePageContext, usePageContextRef } from "../../../../components/navigation/PageContext";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useSdkContext } from "../../../../contexts/sdk/context";

import "./player.css";
import styles from "./player.module.css";
import { MasterUI } from "../../../../components/common/MasterUI";

export interface VodPlayerContentPageProps {
  id: string;
  onBack?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export const VodPlayerContentPage = ({ id, onBack, className, style }: VodPlayerContentPageProps) => {
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [meeting, setMeeting] = useState<MeetingOutputDTO>();
  const [vods, setVods] = useState<VodOutputDTO[]>([]);
  const sdkCtx = useSdkContext();

  const loadVods = async () => {
    if (!id) {
      setError("Internal error");
      return;
    }
    setLoading(true);
    try {
      const meeting = await sdkCtx.client.getHttpClient().meeting.getById(id);
      if (!meeting.data) throw new Error('Meeting not found');
      setMeeting(meeting.data);
      const baseUrl = sdkCtx.client.getHttpClient().getBaseURL();
      const res = await sdkCtx.client.getHttpClient().client.get(`${baseUrl}${baseUrl.endsWith("/") ? "" : "/"}meeting/${id}/vods?vodFilters=state[eq]terminated`, {});
      if (res.status === 200) {
        setError(undefined);
        setVods(res.data ?? []);
      } else {
        setError("Une erreur est survenue");
      }
    } catch (err) {
      setError("Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVods();
  }, []);

  if (loading) return <Loading fullscreen message="Chargement en cours..." style={style} />;

  const renderContent = () => {
    if (error) {
      return (
        <div>
          Une erreur est survenue
          <Touchable onPress={() => loadVods()}>Réessayer</Touchable>
        </div>
      );
    }

    const data: VideoSourceDated[] = [];
    vods.forEach((v) => {
      const isHls = (c?: ContentLocationOutputDTO) => c?.videoType === "hls";
      const getPriority = (c: ContentLocationOutputDTO) => isHls(c) ? 1 : 0;
      const contentLocations = v.contentLocations?.sort((a, b) => getPriority(b) - getPriority(a));
      const contentLocation = contentLocations?.length ? contentLocations[0] : undefined;
      const src = contentLocation?.videoUrl;
      const hls = isHls(contentLocation);
      const device = (v as any).device;
      const name = device?.name; //  ?? "Caméra";
      if (!contentLocation || !src || !name) return;
      data.push({
        src: hls ? { type: "hls", src } : src,
        name,
        startedAt: new Date(v.recordStartedAt),
        endedAt: new Date(v.recordEndedAt),
      });
    });

    let info: React.ReactNode = undefined;
    if (meeting) {
      const expert = meeting.actors?.find((a) => a.externalProvider === "escaet:organizer");
      const expertName = expert?.identity?.username ?? expert?.user?.identity?.username ?? "";
      const meetingName = meeting.name;
      info = (
        <div className={styles.infoContainer}>
          <div className={styles.meetingName}>{meetingName}</div>
          <div className={styles.expertName}>{expertName}</div>
        </div>
      );
    }

    return <MultipleVideoPlayer className={styles.vods} controls={{ header: info }} watermark={{ element: <ReactIcons.LogoFull />, bottom: 20, right: 30 }} data={data} />;
  };
  const classes = [styles.container];
  if (className) classes.push(className);
  return (
    <div className={classes.join(" ")} style={style}>
      {renderContent()}
    </div>
  );
};

export const VodPlayerPage = () => {
  const pageContext = usePageContextRef<SessionMode.MASTER>();
  
  const id = pageContext.current?.params?.id ?? "";

  const onBack = useCallback(() => {
    pageContext.current?.goTo(PageMasterMode.REPLAY_LIST);
  }, []);

  return (
    <MasterUI onBack={onBack}>
      <VodPlayerContentPage id={id} />
    </MasterUI>
  );
};
